<template>
    <div>
        <div
            class="modal fade"
            id="fuzzyMatchModal"
            tabindex="-1"
            aria-labelledby="fuzzyMatchModalLabel"
            aria-hidden="true"
        >
            <div class="modal-dialog modal-lg modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <div>
                            <h5 class="modal-title" id="fuzzyMatchModalLabel">Fuzzy Matches</h5>
                            <div class="modal-subtitle" v-if="part_selected">
                                Selected part: <strong>{{ part_selected }}</strong>
                            </div>
                        </div>
                        <button
                            type="button"
                            class="btn-close"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                            @click="closeModal"
                        ></button>
                    </div>

                    <div class="modal-body">
                        <div v-if="hasMatches" id="fuzzy_part_matches">
                            <div class="match-summary">
                                <span class="fuzzy-count-badge">
                                    {{ fuzzyPartMatches.length }} alternates
                                </span>
                                <span class="selection-count" v-if="selectedAlternate">
                                    {{ selectedAlternate }} selected
                                </span>
                            </div>

                            <div class="table-responsive fuzzy-table-wrapper">
                                <table id="fuzzy_table" class="table table-hover align-middle mb-0">
                                    <thead>
                                        <tr>
                                            <th class="selection-column">Select</th>
                                            <th>Possible Alternate</th>
                                            <th class="text-center distance-column">Difference</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(match, idx) in fuzzyPartMatches" :key="`fuzzy_${idx}`">
                                            <td class="selection-column">
                                                <input
                                                    v-model="selectedAlternate"
                                                    class="form-check-input"
                                                    name="fuzzy-match-selection"
                                                    :value="match.part_alternate"
                                                    type="radio"
                                                    :aria-label="`Select ${match.part_alternate}`"
                                                />
                                            </td>
                                            <td class="alternate-part">{{ match.part_alternate }}</td>
                                            <td class="text-center">{{ match.distance }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div v-else class="empty-state">
                            No fuzzy matches found for this part.
                        </div>
                    </div>

                    <div class="modal-footer fuzzy-modal-actions">
                        <button type="button" class="btn btn-secondary" @click="closeModal">
                            Close
                        </button>
                        <button
                            type="button"
                            class="btn btn-outline-primary fuzzy-action-button"
                            title="Research in a new tab"
                            @click="researchFuzzyMatch"
                            :disabled="!selectedAlternate"
                        >
                            Research in a new tab
                        </button>
                        <button
                            type="button"
                            class="btn btn-primary fuzzy-action-button"
                            title="Load this alternate information as a substitute"
                            @click="replaceFuzzyMatch"
                            :disabled="!selectedAlternate"
                        >
                            Load as substitute
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Modal } from 'bootstrap'

export default {
    name: 'FuzzyMatchModal',
    props: {
        part_selected: {
            type: String,
            default: '',
        },
        fuzzyPartMatches: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            selectedAlternate: '',
        }
    },
    computed: {
        hasMatches() {
            return this.fuzzyPartMatches.length > 0
        },
    },
    watch: {
        fuzzyPartMatches() {
            this.resetModalState()
        },
    },
    methods: {
        replaceFuzzyMatch() {
            this.$emit('replace-fuzzy-match', {
                newPart: [this.selectedAlternate],
                oldPart: this.part_selected,
            })

            this.closeModal()
        },
        researchFuzzyMatch() {
            this.$emit('research-fuzzy-match', [this.selectedAlternate])

            this.closeModal()
        },
        closeModal() {
            const modalElement = document.getElementById('fuzzyMatchModal')
            if (modalElement) {
                const modal = Modal.getInstance(modalElement) || new Modal(modalElement)
                this.resetModalState()
                modal.hide()
            }
        },
        resetModalState() {
            this.selectedAlternate = ''
        },
    },
}
</script>

<style scoped lang="scss">
#fuzzyMatchModal :deep(.modal-content) {
    background: var(--color-surface-canvas);
    border: 1px solid var(--color-border-default);
    color: var(--color-text-primary);
}

#fuzzyMatchModal :deep(.modal-header),
#fuzzyMatchModal :deep(.modal-footer) {
    background: var(--color-surface-canvas);
    border-color: var(--color-border-muted);
    color: var(--color-text-primary);
}

#fuzzyMatchModal :deep(.modal-title) {
    color: var(--color-text-primary);
}

html[data-theme='dark'] #fuzzyMatchModal :deep(.btn-close) {
    filter: invert(1) grayscale(100%) brightness(200%);
}

.modal-subtitle {
    color: var(--color-text-secondary);
    font-size: 0.875rem;
    margin-top: 0.25rem;
}

.match-summary {
    align-items: center;
    display: flex;
    gap: 0.75rem;
    justify-content: space-between;
    margin-bottom: 1rem;
}

.fuzzy-count-badge {
    background: var(--color-brand-primary-soft);
    border: 1px solid var(--color-border-accent-soft);
    border-radius: 999px;
    color: var(--color-brand-primary-strong);
    display: inline-flex;
    font-size: 0.8125rem;
    font-weight: 700;
    line-height: 1;
    padding: 0.4rem 0.65rem;
}

html[data-theme='dark'] .fuzzy-count-badge,
.theme-dark .fuzzy-count-badge {
    background: var(--color-brand-primary);
    border-color: var(--color-border-accent-strong);
    color: var(--color-text-inverse);
}

.selection-count {
    color: var(--color-text-secondary);
    font-size: 0.875rem;
}

.fuzzy-table-wrapper {
    background: var(--color-surface-canvas);
    border: 1px solid var(--color-border-muted);
    border-radius: 0.5rem;
    max-height: 500px;
    overflow: auto;
}

#fuzzy_table {
    color: var(--color-text-primary);
}

#fuzzy_table thead th {
    background: var(--color-surface-inverse);
    border-bottom: 1px solid var(--color-border-strong);
    color: var(--color-text-inverse);
    font-size: 0.8125rem;
    font-weight: 700;
    position: sticky;
    top: 0;
    z-index: 1;
}

#fuzzy_table tbody td {
    background: var(--color-surface-canvas);
    border-color: var(--color-border-muted);
    color: var(--color-text-primary);
}

#fuzzy_table tbody tr:nth-child(even) td {
    background: var(--color-table-stripe);
}

#fuzzy_table tbody tr:hover td {
    background: var(--color-state-highlight-soft);
}

.selection-column {
    text-align: center;
    width: 3rem;
}

.distance-column {
    width: 8rem;
}

.alternate-part {
    font-weight: 600;
}

.form-check-input {
    background-color: var(--color-surface-canvas);
    border-color: var(--color-border-strong);
}

.form-check-input:checked {
    background-color: var(--color-brand-primary);
    border-color: var(--color-brand-primary);
}

.empty-state {
    align-items: center;
    background: var(--color-surface-subtle);
    border: 1px dashed var(--color-border-muted);
    border-radius: 0.5rem;
    color: var(--color-text-secondary);
    display: flex;
    justify-content: center;
    min-height: 10rem;
    text-align: center;
}

.fuzzy-modal-actions {
    display: grid;
    gap: 0.75rem;
    grid-template-columns: minmax(6rem, auto) 1fr 1fr;
    width: 100%;
}

.fuzzy-modal-actions .btn {
    min-height: 2.5rem;
}

.fuzzy-action-button {
    width: 100%;
}

@media (max-width: 576px) {
    .fuzzy-modal-actions {
        grid-template-columns: 1fr;
    }
}

#fuzzyMatchModal :deep(.btn-secondary) {
    background: var(--color-surface-subtle);
    border-color: var(--color-border-default);
    color: var(--color-text-primary);
}

#fuzzyMatchModal :deep(.btn-outline-primary) {
    border-color: var(--color-border-accent-strong);
    color: var(--color-brand-primary);
}

#fuzzyMatchModal :deep(.btn-outline-primary:hover:not(:disabled)) {
    background: var(--color-brand-primary-soft);
    border-color: var(--color-brand-primary);
    color: var(--color-brand-primary-strong);
}

#fuzzyMatchModal :deep(.btn-primary) {
    background: var(--color-brand-primary);
    border-color: var(--color-brand-primary);
    color: var(--color-text-inverse);
}

#fuzzyMatchModal :deep(.btn-primary:hover:not(:disabled)) {
    background: var(--color-brand-primary-strong);
    border-color: var(--color-brand-primary-strong);
    color: var(--color-text-inverse);
}
</style>
